import { useState, useCallback } from "react";
import { ToolboxComponentDropdown as Dropdown } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-dropdown";
import styles from "./size-preset-dropdown-menu.module.css";
import { SizePreset } from "PosterEditor/model/size-presets/size-preset";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import { useTouchMode } from "BEDROCK/mobile";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { formatPrice } from "./size-preset-utils";

interface Props {
  sizePresets: SizePreset[];
  selectedPreset: SizePreset | undefined;
  onSelectPreset: (preset: SizePreset) => void;
  disabled?: boolean;
}

export const SizePresetDropdownMenu = ({
  sizePresets,
  selectedPreset,
  onSelectPreset,
  disabled,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const isTouchMode = useTouchMode();

  const handleSelectPreset = (preset: SizePreset) => {
    onSelectPreset(preset);
    setIsVisible(false);
  };

  const handleToggleVisibility = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const label = (
    <div
      className={filterClasses(
        styles.sizePresetLabel,
        disabled ? styles.disabled : ""
      )}
    >
      <div className={styles.labelContent}>
        <span className={styles.labelText}>
          {selectedPreset?.sizeDescription ?? "Select Size"}
        </span>
        {selectedPreset?.materialDescription && (
          <span className={styles.labelMaterial}>
            {selectedPreset.materialDescription}
          </span>
        )}
      </div>
      <UIIcon
        iconName={isVisible ? "CHEVRON-LEFT" : "CHEVRON-RIGHT"}
        variant="SELECTED"
        size={24}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Dropdown
        isVisible={isVisible}
        onToggleVisibility={handleToggleVisibility}
        label={label}
        position="right"
        dropUp={isTouchMode}
      >
        <div className={styles.sizePresetContainer}>
          {sizePresets.map((preset) => (
            <button
              key={preset.id}
              className={filterClasses(
                styles.presetOption,
                selectedPreset?.id === preset.id
                  ? styles.presetOptionSelected
                  : ""
              )}
              onClick={() => handleSelectPreset(preset)}
            >
              <div className={styles.presetContent}>
                <span className={styles.presetDescription}>
                  {preset.sizeDescription}
                  {preset.materialDescription && (
                    <span className={styles.presetMaterial}>
                      {preset.materialDescription}
                    </span>
                  )}
                </span>
                {preset.priceInt && (
                  <span className={styles.presetPrice}>
                    {formatPrice(preset.priceInt)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </Dropdown>
    </div>
  );
};
